import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatShortTimeDuration } from '../../utils/timeFormat';
import Tooltip from '../Tooltip';
import './TopPagesTable.css';

const TopPagesTable = ({ pages, siteId }) => {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState('pageviews');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  if (!pages || pages.length === 0) {
    return (
      <div className="top-pages-table">
        <div className="table-header">
          <h3>Top Pages</h3>
        </div>
        <div className="no-table-data">
          <p>No page data available</p>
        </div>
      </div>
    );
  }

  const formatNumber = (num) => {
    return num.toLocaleString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const truncateTitle = (title, maxLength = 50) => {
    if (!title || title.length <= maxLength) return title || 'Untitled';
    return title.substring(0, maxLength) + '...';
  };

  const getEngagementColor = (rate) => {
    if (rate >= 70) return 'high';
    if (rate >= 40) return 'medium';
    return 'low';
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="sort-icon inactive">
          <path d="M8 9l4-4 4 4M8 15l4 4 4-4" />
        </svg>
      );
    }
    
    return sortDirection === 'asc' ? (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="sort-icon active">
        <path d="M8 15l4-4 4 4" />
        <path d="M16 11H8" />
      </svg>
    ) : (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="sort-icon active">
        <path d="M8 9l4 4 4-4" />
        <path d="M16 13H8" />
      </svg>
    );
  };

  // Sort pages based on current sort field and direction
  const sortedPages = [...pages].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortField) {
      case 'pageviews':
        aValue = a.pageviews;
        bValue = b.pageviews;
        break;
      case 'users':
        aValue = a.users;
        bValue = b.users;
        break;
      case 'engagementRate':
        aValue = a.engagementRate;
        bValue = b.engagementRate;
        break;
      case 'avgEngagement':
        aValue = a.avgEngagement;
        bValue = b.avgEngagement;
        break;
      case 'revenue':
        aValue = a.revenue || 0;
        bValue = b.revenue || 0;
        break;
      case 'rpm':
        aValue = a.rpm || 0;
        bValue = b.rpm || 0;
        break;
      default:
        return 0;
    }
    
    if (sortDirection === 'asc') {
      return aValue - bValue;
    } else {
      return bValue - aValue;
    }
  });

  // Pagination logic
  const totalPages = Math.ceil(sortedPages.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPages = sortedPages.slice(startIndex, endIndex);
  const totalItems = sortedPages.length;

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const hasEstimatedRevenue = pages.some(p => p.isEstimated);

  return (
    <div className="top-pages-table">
      <div className="table-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <h3>Top Pages</h3>
          {hasEstimatedRevenue && (
            <span style={{ fontSize: '13px', color: '#666', fontStyle: 'italic' }}>
              Revenue and RPM are estimated
            </span>
          )}
        </div>
        <span className="page-count">
          Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} pages
          {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
        </span>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th className="col-page">Page</th>
              <th className="col-number sortable" onClick={() => handleSort('pageviews')}>
                <div className="header-with-tooltip">
                  Views
                  <Tooltip content={`Click to sort by pageviews ${sortField === 'pageviews' ? (sortDirection === 'asc' ? '(ascending)' : '(descending)') : ''}`}>
                    {getSortIcon('pageviews')}
                  </Tooltip>
                </div>
              </th>
              <th className="col-number sortable" onClick={() => handleSort('users')}>
                <div className="header-with-tooltip">
                  Users
                  <Tooltip content={`Click to sort by users ${sortField === 'users' ? (sortDirection === 'asc' ? '(ascending)' : '(descending)') : ''}`}>
                    {getSortIcon('users')}
                  </Tooltip>
                </div>
              </th>
              <th className="col-number sortable" onClick={() => handleSort('engagementRate')}>
                <div className="header-with-tooltip">
                  Engagement
                  <Tooltip content="Percentage of sessions where users were engaged with this page. Higher engagement indicates better content quality and user experience.">
                    <div className="tooltip-icon">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 16v-4M12 8h.01" />
                      </svg>
                    </div>
                  </Tooltip>
                  <Tooltip content={`Click to sort by engagement rate ${sortField === 'engagementRate' ? (sortDirection === 'asc' ? '(ascending)' : '(descending)') : ''}`}>
                    {getSortIcon('engagementRate')}
                  </Tooltip>
                </div>
              </th>
              <th className="col-number sortable" onClick={() => handleSort('avgEngagement')}>
                <div className="header-with-tooltip">
                  Avg. Time
                  <Tooltip content="Average time users spend engaged with this page content. Longer times suggest more valuable or engaging content.">
                    <div className="tooltip-icon">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 16v-4M12 8h.01" />
                      </svg>
                    </div>
                  </Tooltip>
                  <Tooltip content={`Click to sort by average time ${sortField === 'avgEngagement' ? (sortDirection === 'asc' ? '(ascending)' : '(descending)') : ''}`}>
                    {getSortIcon('avgEngagement')}
                  </Tooltip>
                </div>
              </th>
              {pages.some(p => p.revenue !== undefined && p.revenue > 0) && (
                <>
                  <th className="col-number sortable" onClick={() => handleSort('revenue')}>
                    <div className="header-with-tooltip">
                      Revenue
                      <Tooltip content="Total AdSense revenue generated from this page">
                        <div className="tooltip-icon">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <path d="M12 16v-4M12 8h.01" />
                          </svg>
                        </div>
                      </Tooltip>
                      <Tooltip content={`Click to sort by revenue ${sortField === 'revenue' ? (sortDirection === 'asc' ? '(ascending)' : '(descending)') : ''}`}>
                        {getSortIcon('revenue')}
                      </Tooltip>
                    </div>
                  </th>
                  <th className="col-number sortable" onClick={() => handleSort('rpm')}>
                    <div className="header-with-tooltip">
                      RPM
                      <Tooltip content="Revenue per 1,000 pageviews. Higher RPM indicates better monetization per pageview">
                        <div className="tooltip-icon">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <path d="M12 16v-4M12 8h.01" />
                          </svg>
                        </div>
                      </Tooltip>
                      <Tooltip content={`Click to sort by RPM ${sortField === 'rpm' ? (sortDirection === 'asc' ? '(ascending)' : '(descending)') : ''}`}>
                        {getSortIcon('rpm')}
                      </Tooltip>
                    </div>
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {paginatedPages.map((page, index) => (
              <tr 
                key={page.contentId || startIndex + index}
                onClick={() => {
                  if (page.contentId && siteId) {
                    navigate(`/page/${page.contentId}?siteId=${siteId}`);
                  }
                }}
                onContextMenu={(e) => {
                  if (page.contentId && siteId) {
                    e.preventDefault();
                    const url = `/page/${page.contentId}?siteId=${siteId}`;
                    window.open(url, '_blank');
                  }
                }}
                style={{ cursor: page.contentId && siteId ? 'pointer' : 'default' }}
              >
                <td className="col-page">
                  <div className="page-info">
                    <span className="page-rank">#{startIndex + index + 1}</span>
                    <div className="page-details">
                      <div className="page-title">
                        {truncateTitle(page.pageTitle)}
                      </div>
                      <div className="page-path">{page.pagePath}</div>
                    </div>
                  </div>
                </td>
                <td className="col-number">
                  <strong>{formatNumber(page.pageviews)}</strong>
                </td>
                <td className="col-number">
                  {formatNumber(page.users)}
                </td>
                <td className="col-number">
                  <Tooltip content={`${(page.engagementRate || 0).toFixed(1)}% of sessions on this page were engaged. This means ${Math.round(((page.engagementRate || 0) / 100) * (page.sessions || 0))} out of ${page.sessions || 0} sessions lasted longer than 10 seconds, had 2+ pageviews, or included a conversion.`}>
                    <span className={`engagement-badge ${getEngagementColor(page.engagementRate || 0)}`}>
                      {(page.engagementRate || 0).toFixed(0)}%
                    </span>
                  </Tooltip>
                </td>
                <td className="col-number">
                  <Tooltip content={`Users spend an average of ${formatShortTimeDuration(page.avgEngagement || 0)} engaged with this page content. This includes time reading, scrolling, and interacting with the page.`}>
                    <span className="avg-time-value">
                      {formatShortTimeDuration(page.avgEngagement || 0)}
                    </span>
                  </Tooltip>
                </td>
                {pages.some(p => p.revenue !== undefined && p.revenue > 0) && (
                  <>
                    <td className="col-number">
                      {page.revenue !== undefined && page.revenue > 0 ? (
                        <div>
                          <strong>{formatCurrency(page.revenue)}</strong>
                          {page.isEstimated && (
                            <Tooltip content="Revenue is estimated based on site-wide RPM and pageviews. Actual revenue per page may vary.">
                              <span style={{ fontSize: '11px', color: '#666', marginLeft: '4px', cursor: 'help' }}>~</span>
                            </Tooltip>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#999' }}>—</span>
                      )}
                    </td>
                    <td className="col-number">
                      {page.rpm !== undefined && page.rpm > 0 ? (
                        <span>${page.rpm.toFixed(2)}</span>
                      ) : (
                        <span style={{ color: '#999' }}>—</span>
                      )}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="pagination-controls" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginTop: '20px', 
          padding: '15px',
          borderTop: '1px solid #e0e0e0'
        }}>
          <button
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            style={{
              padding: '8px 16px',
              backgroundColor: currentPage === 1 ? '#f0f0f0' : '#007bff',
              color: currentPage === 1 ? '#999' : 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            ← Previous
          </button>
          <span style={{ fontSize: '14px', color: '#666' }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            style={{
              padding: '8px 16px',
              backgroundColor: currentPage === totalPages ? '#f0f0f0' : '#007bff',
              color: currentPage === totalPages ? '#999' : 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default TopPagesTable;

